import { supabase } from '@/integrations/supabase/client';

/**
 * Extracts the filename from a Supabase storage URL
 */
function extractFilenameFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  try {
    if (!url.includes('/')) {
      return url;
    }

    const urlParts = url.split('/');
    const filename = urlParts[urlParts.length - 1];
    return filename.split('?')[0];
  } catch (error) {
    console.error('Error extracting filename from URL:', url, error);
    return null;
  }
}

/**
 * Deletes a single image from Supabase storage
 */
async function deleteImageFromStorage(imageUrl: string | null | undefined): Promise<boolean> {
  if (!imageUrl) return false;

  const filename = extractFilenameFromUrl(imageUrl);
  if (!filename) {
    console.warn('Could not extract filename from URL:', imageUrl);
    return false;
  }

  try {
    const { error } = await supabase
      .storage
      .from('business-assets')
      .remove([filename]);

    if (error) {
      console.error('Error deleting image from storage:', filename, error);
      return false;
    }

    console.log('Successfully deleted image:', filename);
    return true;
  } catch (error) {
    console.error('Error deleting image from storage:', filename, error);
    return false;
  }
}

/**
 * Checks if a URL is from Supabase storage (not an external API)
 */
function isSupabaseStorageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.includes('supabase.co/storage/');
}

/**
 * Collects all image URLs from a coupon offer
 * Only includes URLs from Supabase storage
 */
export function collectCouponImageUrls(coupon: any): string[] {
  const urls: string[] = [];

  // Single URL fields (legacy - only if from Supabase storage)
  if (coupon.logo_url && isSupabaseStorageUrl(coupon.logo_url)) {
    urls.push(coupon.logo_url);
  }
  if (coupon.hero_image_url && isSupabaseStorageUrl(coupon.hero_image_url)) {
    urls.push(coupon.hero_image_url);
  }
  if (coupon.google_logo_url && isSupabaseStorageUrl(coupon.google_logo_url)) {
    urls.push(coupon.google_logo_url);
  }

  // Multi-resolution fields
  if (coupon.apple_logo_urls) {
    if (coupon.apple_logo_urls['1x']) urls.push(coupon.apple_logo_urls['1x']);
    if (coupon.apple_logo_urls['2x']) urls.push(coupon.apple_logo_urls['2x']);
    if (coupon.apple_logo_urls['3x']) urls.push(coupon.apple_logo_urls['3x']);
  }

  if (coupon.hero_image_urls) {
    if (coupon.hero_image_urls['1x']) urls.push(coupon.hero_image_urls['1x']);
    if (coupon.hero_image_urls['2x']) urls.push(coupon.hero_image_urls['2x']);
    if (coupon.hero_image_urls['3x']) urls.push(coupon.hero_image_urls['3x']);
  }

  return urls;
}

/**
 * Deletes all images associated with a coupon offer
 */
export async function deleteAllCouponImages(coupon: any): Promise<void> {
  const urls = collectCouponImageUrls(coupon);

  console.log(`Deleting ${urls.length} images for coupon offer:`, coupon.id);

  // Delete all images in parallel
  const results = await Promise.allSettled(
    urls.map(url => deleteImageFromStorage(url))
  );

  // Log results
  const successful = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
  const failed = results.length - successful;

  console.log(`Image deletion complete: ${successful} successful, ${failed} failed`);
}

/**
 * Deletes old images that were replaced during an update
 */
export async function deleteReplacedCouponImages(
  oldCoupon: any,
  newImages: {
    appleLogoUrls?: { '1x': string; '2x': string; '3x': string } | null;
    googleLogoUrl?: string | null;
    heroImageUrls?: { '1x': string; '2x': string; '3x': string } | null;
  }
): Promise<void> {
  const urlsToDelete: string[] = [];

  // If Apple logo was replaced, delete old ones
  if (newImages.appleLogoUrls && oldCoupon.apple_logo_urls) {
    if (oldCoupon.apple_logo_urls['1x']) urlsToDelete.push(oldCoupon.apple_logo_urls['1x']);
    if (oldCoupon.apple_logo_urls['2x']) urlsToDelete.push(oldCoupon.apple_logo_urls['2x']);
    if (oldCoupon.apple_logo_urls['3x']) urlsToDelete.push(oldCoupon.apple_logo_urls['3x']);
  }

  // If Google logo was replaced, delete old one
  if (newImages.googleLogoUrl && oldCoupon.google_logo_url && isSupabaseStorageUrl(oldCoupon.google_logo_url)) {
    urlsToDelete.push(oldCoupon.google_logo_url);
  }

  // If hero image was replaced, delete old ones
  if (newImages.heroImageUrls && oldCoupon.hero_image_urls) {
    if (oldCoupon.hero_image_urls['1x']) urlsToDelete.push(oldCoupon.hero_image_urls['1x']);
    if (oldCoupon.hero_image_urls['2x']) urlsToDelete.push(oldCoupon.hero_image_urls['2x']);
    if (oldCoupon.hero_image_urls['3x']) urlsToDelete.push(oldCoupon.hero_image_urls['3x']);
  }

  if (urlsToDelete.length === 0) {
    console.log('No old images to delete');
    return;
  }

  console.log(`Deleting ${urlsToDelete.length} replaced images`);

  // Delete all old images in parallel
  const results = await Promise.allSettled(
    urlsToDelete.map(url => deleteImageFromStorage(url))
  );

  // Log results
  const successful = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
  const failed = results.length - successful;

  console.log(`Replaced image deletion complete: ${successful} successful, ${failed} failed`);
}
